import { Activity } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import MobileSection from './MobileSection';
import ProfileSection from './ProfileSection';
import LocalizationSection from './LocalizationSection';
import MegaMenuSection from './MegaMenuSection';
import FullScreenSection from './FullScreenSection';
import NotificationSection from './NotificationSection';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';

// assets
import { IconMenu2 } from '@tabler/icons-react';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

export default function Header() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state: { menuOrientation }
  } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  return (
    <>
      {/* logo & toggler button */}
      {/*
        ⚠ THE LOGO IS NO LONGER HIDDEN BELOW `md`. Berry dropped it on a phone and left the burger as the
        top-left item, so a tenant's mark was on the public page, gone the moment you signed in, and back
        again when you rotated to a tablet. The one place a white-label platform cannot afford to drop the
        tenant's name is the small screen most of their users are on.

        ⚠ WHICH MEANS THE ROW HAS TO GIVE THE LOGO ROOM IT DOES NOT HAVE. Measured at 375px: burger 34,
        search 50, notifications 34, the profile chip 90 and the overflow dots 52, with 16px of Toolbar
        padding each side — 52px of slack in the two `flexGrow` spacers and not one pixel more. So the logo
        takes that slack and then gives, rather than pushing a control off the edge:

        - `flexShrink: 100` on THIS box (mobile only) weights essentially all of the row's shrinkage onto
          the logo — the same factor-not-ceiling argument `.te-wordmark--logo` makes on the public site. A
          fixed width tuned against one tenant's logo cannot know what the next tenant uploads.
        - `minWidth: 0` because a flex item's automatic minimum is its CONTENT — without it the box refuses
          to go below the image's intrinsic width and nothing shrinks at all.
        - the image itself already carries `maxWidth: min(180px, 100%)` (see ui-component/Logo), and the
          `100%` half is what turns pressure on this box into a smaller logo instead of an overflow.

        ⚠ `alignItems: 'center'` IS A FIX, NOT TIDYING. The default is `normal`/stretch and the toggle has
        its own 34px height, so beside a 55px logo it was pinned to the TOP of the row — measured, both at
        y=16 in a 55px box — about 10px above the logo's optical centre.
      */}
      <Box
        sx={{
          width: downMD ? 'auto' : 228,
          display: 'flex',
          alignItems: 'center',
          gap: downMD ? 1 : 0,
          minWidth: 0,
          flexShrink: downMD ? 100 : 0
        }}
      >
        <Box component="span" sx={{ display: 'block', flexGrow: 1, minWidth: 0 }}>
          <LogoSection />
        </Box>
        <Activity mode={!isHorizontal ? 'visible' : 'hidden'}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              // the logo absorbs the row's shrinkage (see above); the toggle is a 34px tap target and must
              // never be the thing that gives.
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'all .2s ease-in-out',
              color: theme.vars.palette.secondary.dark,
              background: theme.vars.palette.secondary.light,
              '&:hover': {
                color: theme.vars.palette.secondary.light,
                background: theme.vars.palette.secondary.dark
              },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.secondary.main,
                background: theme.vars.palette.dark.main,
                '&:hover': {
                  color: theme.vars.palette.secondary.light,
                  background: theme.vars.palette.secondary.main
                }
              })
            }}
            onClick={() => handlerDrawerOpen(!drawerOpen)}
          >
            <IconMenu2 stroke={1.5} size="20px" />
          </Avatar>
        </Activity>
      </Box>

      {/* header search */}
      <SearchSection />
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />

      {/* mega-menu */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <MegaMenuSection />
      </Box>

      {/* live customization & localization */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <LocalizationSection />
      </Box>

      {/* notification */}
      <NotificationSection />

      {/* full sceen toggler */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <FullScreenSection />
      </Box>

      {/* profile */}
      <ProfileSection />

      {/* mobile header */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <MobileSection />
      </Box>
    </>
  );
}
